/**
 * Time budgets for best-effort async work that must never block a critical
 * user-facing step.
 *
 * Two places rely on this:
 * - the pre-mount bootstrap in `main.tsx`: anything awaited before the first
 *   paint has to give up eventually, because embedded in-app browsers
 *   (Instagram's and Facebook's WebViews) routinely leave a request hanging
 *   instead of failing it, and a bare `await` there means a permanently blank
 *   page under the host app's spinner;
 * - the paywall checkout handlers: the "checkout initiated" log should flush
 *   before we navigate to the external checkout server, but a hanging log
 *   write must never keep the user from reaching checkout.
 */

export type TimeBudgetOutcome =
  | { status: "settled" }
  | { status: "failed"; error: unknown }
  | { status: "timed_out" };

/**
 * Wait for `work`, but never longer than `budgetMs`.
 *
 * Never rejects: a rejection is reported as `failed` so the caller can proceed
 * anyway. Work that times out is not cancelled - it keeps running in the
 * background, it just stops holding up the caller.
 */
export async function awaitWithinTimeBudget(
  work: Promise<unknown>,
  budgetMs: number
): Promise<TimeBudgetOutcome> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const expiry = new Promise<TimeBudgetOutcome>((resolve) => {
    timeoutId = setTimeout(() => resolve({ status: "timed_out" }), budgetMs);
  });

  try {
    return await Promise.race([
      work.then(
        (): TimeBudgetOutcome => ({ status: "settled" }),
        (error: unknown): TimeBudgetOutcome => ({ status: "failed", error })
      ),
      expiry,
    ]);
  } finally {
    clearTimeout(timeoutId);
  }
}
